import { useState, useEffect, useContext, useRef } from "react";
import {
  Avatar,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Image,
  Input,
  Modal,
  Row,
  Select,
  Upload as UploadAntd,
  message,
  Badge,
} from "antd";
import {
  DownOutlined,
  ExclamationCircleOutlined,
  KeyOutlined,
  LogoutOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import notfound from "../../assets/images/not_found.png";
import { NavLink, useNavigate } from "react-router-dom";
import DeleteModal from "../../components/DeleteModal";
import { Severty, ShowToast } from "../../helper/toast";
import headerLogo from "../../assets/images/imerge.png";
import { AuthContext } from "../../context/AuthContext";
import Notification from "../../assets/images/notification.svg";
import defaultLogo from "../../assets/images/user-default-iconnn.png";
import { useAppContext } from "../../context/AppContext";
import lang from "../../helper/lang";
import Prouser from "../../assets/images/user.png";
import apiPath from "../../constants/apiPath";
import axios from "axios";

const Search = Input.Search;
const { Option } = Select;

const STATUS_META = {
  online: { color: "#52c41a", buttonText: "Online", menuText: "Online" },
  away: { color: "#faad14", buttonText: "Away", menuText: "Away / Idle" },
  busy: { color: "#f5222d", buttonText: "Busy", menuText: "Busy / DND" },
  offline: { color: "#d9d9d9", buttonText: "Offline", menuText: "Offline" },
};
const IDLE_AWAY_MS = 5 * 60 * 1000;

function StatusDot({ color }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        marginRight: 8,
        backgroundColor: color,
      }}
    />
  );
}

function getStatusMeta(status) {
  return STATUS_META[status] || STATUS_META.offline;
}

function buildStatusItems() {
  return ["online", "away", "busy"].map((statusKey) => {
    const status = getStatusMeta(statusKey);
    return {
      key: statusKey,
      label: (
        <span className="d-flex align-items-center">
          <StatusDot color={status.color} />
          {lang(status.menuText)}
        </span>
      ),
    };
  });
}
const toggler = [
  <svg
    width="20"
    height="20"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 448 512"
    key={0}
  >
    <path d="M16 132h416c8.837 0 16-7.163 16-16V76c0-8.837-7.163-16-16-16H16C7.163 60 0 67.163 0 76v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16z"></path>
  </svg>,
];
// Uploads now go through the backend (POST /admin/media/upload), which
// holds the real AWS credentials server-side and never exposes them. This
// used to upload straight to S3 from the browser with a hardcoded access
// key + secret baked into this file — visible to anyone via view-source on
// the deployed site, no login required. See MediaUploadController.ts.
function Header({ onPress, setToggle, isSidenavCollapsed }) {
  const [visible, setVisible] = useState(false);
  const [profileVisible, setProfileVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [appSettingVisible, setAppSettingVisible] = useState(false);
  const { logout } = useContext(AuthContext);
  const [profile, setProfile] = useState({});
  const { request } = useRequest();
  const [selected, setSelected] = useState();
  const [refresh, setRefresh] = useState(false);
  const [commission, setCommission] = useState();
  const [nextPayoutDate, setNextPayoutDate] = useState();
  const [payoutFrequency, setPayoutFrequency] = useState();

  const [desCommission, setDesCommission] = useState();
  const [desNextPayoutDate, setDesNextPayoutDate] = useState();
  const [desPayoutFrequency, setDesPayoutFrequency] = useState();
  // New states for Minimum Amount & Payment Methods
  const [minimumAmount, setMinimumAmount] = useState();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const navigate = useNavigate();
  const { confirm } = Modal;
  const { notification, notificationCount, setRefreshNotification, language } =
    useAppContext();
  const [isLogoutModalVisible, setIsLogoutModalVisible] = useState(false);

  const storedProfile = JSON.parse(localStorage.getItem("admin_userProfile")) || {};
  const [userStatus, setUserStatus] = useState(storedProfile?.currentStatus || "online");
  const userStatusRef = useRef(userStatus);
  useEffect(() => {
    userStatusRef.current = userStatus;
  }, [userStatus]);

  const updateStatus = (key) => {
    request({
      url: apiPath.currentStatus,
      method: "PUT",
      data: { currentStatus: key },
      onSuccess: () => setUserStatus(key),
      onError: () => {},
    });
  };

  const handleStatusChange = ({ key }) => updateStatus(key);

  // Presence: away after 5 minutes with no mouse/keyboard/touch activity,
  // back to online the moment activity resumes. A manually-set "busy" is
  // left alone — only "online"/"away" are auto-managed. Header stays
  // mounted for the whole authenticated session, so this effect only
  // needs to run once; refs carry the latest status into the listeners.
  useEffect(() => {
    let idleTimer;

    const goAway = () => {
      if (userStatusRef.current === "online") updateStatus("away");
    };

    const resetTimer = () => {
      if (userStatusRef.current === "away") updateStatus("online");
      clearTimeout(idleTimer);
      idleTimer = setTimeout(goAway, IDLE_AWAY_MS);
    };

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((evt) => window.addEventListener(evt, resetTimer));
    idleTimer = setTimeout(goAway, IDLE_AWAY_MS);

    return () => {
      clearTimeout(idleTimer);
      events.forEach((evt) => window.removeEventListener(evt, resetTimer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusItems = buildStatusItems();
  const activeStatus = getStatusMeta(userStatus);

  useEffect(() => {
    request({
      url: "/admin/auth/get-profile",
      method: "GET",
      onSuccess: (data) => {
        console.log(data);
        setProfile(data.data);
        setSelected(data.data);
        setCommission(data.data?.vendor_commission || 0);
        setPayoutFrequency(data.data?.payoutFrequency || 0);
        setNextPayoutDate(data.data?.nextPayoutDate || "");

        setDesCommission(data.data?.designer_commission || 0);
        setDesPayoutFrequency(data.data?.desPayoutFrequency || 0);
        setDesNextPayoutDate(data.data?.desNextPayoutDate || "");

        // Set Minimum Amount & Payment Methods
        setMinimumAmount(data.data?.minimum_amount || 0);
        setPaymentMethods(data.data?.paymentMethods || []);
      },
      onError: (error) => {
        console.log(error);
        ShowToast(error, Severty.ERROR);
      },
    });
  }, [refresh]);
  useEffect(() => {
    if (!isOpen) return document.body.classList.remove("edit-dropdown");
    document.body.classList.add("edit-dropdown");

    return () => {
      document.body.classList.remove("edit-dropdown");
    };
  }, [isOpen]);
  const items = [
    {
      label: lang("Manage") + " " + lang("Profile"),
      key: "1",
      icon: <UserOutlined />,
    },
    {
      label: lang("Change") + " " + lang("Password"),
      key: "2",
      icon: <KeyOutlined />,
    },
    {
      label: lang("Logout"),
      key: "3",
      icon: <LogoutOutlined />,
    },
  ];
  const [notificationReadCount, setNotificationReadCount] = useState(0);
  const fetchNotificationCount = async () => {
    try {
      request({
        url: "/admin/auth/notification/count",
        method: "GET",
        onSuccess: (data) => {
          setNotificationReadCount(data?.data?.notificationReadCount);
          console.log("notificationReadCount", data?.count);
        },
        onError: (error) => {
          console.error("Error fetching notification count:", error);
        },
      });
    } catch (error) {
      console.error("Error fetching notification count:", error);
    }
  };

  useEffect(() => {
    fetchNotificationCount();
  }, []);

  const showDeleteConfirm = (record) => {
    setIsLogoutModalVisible(true);
  };
  const showConfirm = () => {
    setTimeout(() => {
      confirm({
        icon: <ExclamationCircleOutlined />,
        content: <Button>Are you sure you want to logout?</Button>,
        onOk() {
          logout();
        },
        onCancel() {
          console.log("Cancel");
        },
      });
    }, 5);
  };
  const { name } = JSON.parse(localStorage.getItem("admin_userProfile")) || {};
  console.log("notificationReadCount", notificationReadCount);
  console.log("notification", notificationCount);

  const notificationitems = [
    {
      label: (
        <div className="notification_top">
          <div className="notification-head">
            <h5>{lang("Notifications")}</h5>
          </div>
          <div className="notification-inner">
            {notification?.length ? (
              notification?.map((item) => (
                <div
                  key={item._id}
                  className="single-notification"
                  // onClick={() => handleNotiNavigate(item)}
                >
                  <div className="notification-img">
                    <img
                      src={item?.from_id.image ? item?.from_id.image : Prouser}
                    />
                  </div>
                  <div className="notification-cont">
                    {/* <p>
                  <span>Vendor Heeba Khan</span> attach a deposit receipt to the
                  App bank account of <span>AED 100.00</span>
                </p> */}
                    <p>
                      <span>{item.title ? item.title : ""}</span>
                      <p>{item.description ? item.description : ""}</p>
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <span>{lang("No Notification")}</span>
            )}
          </div>
          {notification?.length ? (
            <div className="viewAll_notification">
              <Button
                onClick={() => navigate("/notification")}
                className="btnStyle btn_primary"
              >
                {lang("View All")}
              </Button>
            </div>
          ) : (
            ""
          )}
        </div>
      ),
    },
  ];
  const handleMenuClick = (e) => {
    switch (e.key) {
      case "1":
        setProfileVisible(true);
        break;
      case "2":
        setVisible(true);
        break;
      case "3":
        showConfirm();
        break;

      case "4":
        setAppSettingVisible(true);
        break;
      default:
        break;
    }
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };
  return (
    <>
      <Row gutter={[24, 0]} className="align-items-center">
        {/* Left Side: Logo & Toggle */}
        <Col span={24} xs={18} md={12} xl={12} lg={12}>
          <div className="d-flex align-items-center gap-3">
            <Button
              type="link"
              className="sidebar-toggler ps-0 d-block d-lg-none"
              onClick={() => onPress()}
              aria-label="Open navigation sidebar"
            >
              {toggler}
            </Button>
            <h6 className="admin-header-greeting mb-0">
              {(() => {
                const h = new Date().getHours();
                const g = h < 12 ? "Good Morning" : h < 17 ? "Good Afternoon" : "Good Evening";
                const fname = (selected?.name || "Admin").split(" ")[0];
                return `${g}, ${fname}`;
              })()}
            </h6>
          </div>
        </Col>

        {/* Right Side: Notification & Toggler */}
        <Col
          span={24}
          xs={6}
          md={12}
          xl={12}
          lg={12}
          className="header-control d-flex justify-content-end align-items-center gap-3"
        >
          <div className="mt-1 for-align-class">
            <Dropdown menu={{ items: statusItems, onClick: handleStatusChange }} trigger={["click"]}>
              <Button className="ant-btn" size="small">
                <StatusDot color={activeStatus.color} />
                {lang(activeStatus.buttonText)}
                <DownOutlined style={{ marginLeft: 8 }} />
              </Button>
            </Dropdown>
          </div>
          <div className="profileDropdownMain">
            <Dropdown menu={menuProps}>
              <Button className="ant-btn ant-btn-default ant-dropdown-trigger ant-dropdown-open">
                <div className="userName">{selected?.name}</div>
                <div className="userEmail">{selected?.email}</div>
              </Button>
            </Dropdown>
            <Image src={selected?.profilePic} preview={false} />
          </div>
          {/* Notification Button */}
          {/* <div className="notificationDropdownMain">
            <Button
              onClick={() => navigate("/notification")}
              style={{ position: "relative" }}
            >
              <Badge
                count={notificationReadCount}
                size="small"
                offset={[-5, 5]}
              >
                <img
                  src={Notification}
                  alt="Notification Icon"
                  style={{ width: "18px", height: "18px" }}
                />
              </Badge>
            </Button>
          </div> */}
        </Col>
      </Row>

      {isLogoutModalVisible && (
        <DeleteModal
          title="Logout"
          subtitle="Are you sure you want to logout?"
          show={isLogoutModalVisible}
          hide={() => setIsLogoutModalVisible(false)}
          onOk={() => {
            logout();
          }}
        />
      )}

      {profileVisible && (
        <EditProfile
          show={profileVisible}
          hide={() => setProfileVisible(false)}
          data={selected}
          refresh={() => setRefresh((prev) => !prev)}
        />
      )}

      {visible && (
        <ChangePassword
          show={visible}
          hide={() => setVisible(false)}
          refresh={() => setRefresh((prev) => !prev)}
        />
      )}
    </>
  );
}

const EditProfile = ({ show, hide, data, refresh }) => {
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState([]);

  const beforeUpload = (file) => {
    // console.log(file.type, "file type>>>>>>.")
    // if (fileType.includes(file.type)) {

    // } else {
    //   message.error("File format is not correct")
    //   return false
    // }
    const isLt5M = file.size / 1024 / 1024 < 5;

    if (!isLt5M) {
      // ShowToast.error(`Image must be smaller than 5 MB!`)
      return false;
    }
    return true;
  };

  const uploadFileToS3 = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post(`${apiPath.baseURL}admin/media/upload`, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
        "Content-Type": "multipart/form-data",
      },
    });
    return { Location: res.data?.data?.url };
  };

  const handleChange = async (event) => {
    const { file } = event;

    let validateImage =
      file.type.includes("image") &&
      !file.type.includes("svg") &&
      !file.type.includes("gif");

    if (!validateImage) {
      return ShowToast("Media not supported " + file.type, Severty.ERROR);
    }

    const isValidSize = beforeUpload(file);
    if (!isValidSize) {
      return ShowToast("Image must be smaller than 5 MB!", Severty.ERROR);
    }

    setFile([{ uid: file.uid, name: file.name, status: "uploading" }]); // ✅ Ensure `uid` exists

    try {
      const uploadResponse = await uploadFileToS3(
        file
      );

      if (!uploadResponse || !uploadResponse.Location) {
        throw new Error("Upload failed");
      }

      setFile([
        {
          uid: file.uid, // ✅ Ensure `uid` exists
          name: file.name,
          status: "done",
          url: uploadResponse.Location, // ✅ Correct file URL
          thumbUrl: uploadResponse.Location, // ✅ Ensure preview works
        },
      ]);

      console.log(uploadResponse);
    } catch (err) {
      console.error(err);
      setFile([]); // ✅ Reset file if upload fails
    }
  };

  useEffect(() => {
    if (!data) return;

    form.setFieldsValue({ ...data });

    if (data.profilePic) {
      setFile([
        {
          uid: "1", // ✅ Ensure `uid` exists
          name: "profile.jpg",
          status: "done",
          url: data.profilePic,
          thumbUrl: data.profilePic,
        },
      ]);
    } else {
      setFile([]);
    }
  }, [data]);
  const onEditProfile = (values) => {
    const { email, name } = values;
    if (file.length <= 0)
      return ShowToast("Please select the profile Image ", Severty.ERROR);
    const payload = {};
    setLoading(true);
    payload.email = email;
    payload.name = name;
    payload.profilePic = file.length > 0 ? file[0].url : null;
    request({
      url: "/admin/auth/update-profile",
      method: "POST",
      data: payload,
      onSuccess: (data) => {
        setLoading(false);
        if (data.status) {
          ShowToast(data.message, Severty.SUCCESS);
          hide();
          refresh();
          // hide()
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (error) => {
        ShowToast(error.response.data.message, Severty.ERROR);
        // console.log(error.response.data, "Error")
        setLoading(false);
      },
    });
  };
  return (
    <Modal
      visible={show}
      title={`${data ? "Edit Profile" : ""}`}
      okText="Ok"
      onCancel={hide}
      okButtonProps={{
        form: "create",
        htmlType: "submit",
        loading: loading,
      }}
    >
      <Form id="create" form={form} onFinish={onEditProfile} layout="vertical">
        <Row>
          <Col span={24}>
            <Form.Item
              label="Name"
              name="name"
              rules={[
                { required: true, message: "Please input the name!" },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === "") {
                      return Promise.reject("Name cannot be only spaces");
                    }
                    return Promise.resolve();
                  },
                },
                {
                  pattern: /^[a-zA-Z ]*$/,
                  message: "Only alphabetic characters allowed",
                },
                {
                  max: 50,
                  message: "Name cannot exceed 50 characters",
                },
              ]}
            >
              <Input placeholder="Name" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                {
                  type: "email",
                  message: "The input is not valid E-mail!",
                },
                { required: true, message: "Please input the email!" },
                {
                  max: 100,
                  message: "Email cannot exceed 100 characters",
                },
              ]}
            >
              <Input placeholder={"Email"} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <UploadAntd
              listType="picture"
              maxCount={1}
              customRequest={handleChange}
              // onChange={handleChange}
              onRemove={() => setFile([])} // ✅ Ensure file state resets
              fileList={file} // ✅ Ensure it always contains valid objects
            >
              {file.length > 0 ? null : (
                <Button icon={<UploadOutlined />}>Upload</Button>
              )}
            </UploadAntd>
            <p className="main-new-upload-graph-name-insturction">
              **Logo should be (300-800)X(300-800) for best view in profile
              image. You can select only ( .png, .jpg) format files upto 5 MB
              file size..!!!
            </p>
          </Col>
        </Row>

        {/* <Form.Item name="description" label="Description">
            <Input type="textarea" />
          </Form.Item> */}

        {/* <Form.Item name="type" label="Type">
            <Radio.Group>
              <Radio value="public">Public</Radio>
              
              <Radio value="private">Private</Radio>
            </Radio.Group>
          </Form.Item> */}
      </Form>
    </Modal>
  );
};

const ChangePassword = ({ show, hide }) => {
  const [form] = Form.useForm();
  const { request } = useRequest();
  const { logout } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const handleCreate = (values) => {
    const { old_password, new_password } = values;
    const payload = {};
    if (!old_password || !new_password)
      return ShowToast("Please enter password ", Severty.ERROR);
    setLoading(true);
    payload.new_password = new_password;
    payload.old_password = old_password;
    request({
      url: "/admin/auth/change-password",
      method: "POST",
      data: payload,
      onSuccess: (data) => {
        setLoading(false);
        if (data.status) {
          hide();
          ShowToast(data.message, Severty.SUCCESS);
          setTimeout(() => {
            logout();
          }, 2000);
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (error) => {
        ShowToast(error.response.data.message, Severty.ERROR);
        // console.log(error.response.data, "Error")
        setLoading(false);
      },
    });
  };

  return (
    <Modal
      open={show}
      title="Change password"
      okText="Ok"
      onCancel={() => {
        hide();
        form.resetFields();
      }}
      okButtonProps={{
        form: "create",
        htmlType: "submit",
        loading: loading,
      }}
    >
      <Form
        id="create"
        form={form}
        layout="vertical"
        onFinish={handleCreate}
        className="change-pw"
        disabled={loading}
      >
        <Form.Item
          label="Old Password"
          name="old_password"
          hasFeedback
          rules={[
            { required: true, message: "Please input the old password!" },
            {
              validator: (_, value) =>
                value && value.trim()
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error("Password cannot be only spaces!")
                    ),
            },
          ]}
        >
          <Input.Password maxLength={50} placeholder="Old Password" />
        </Form.Item>
        <Form.Item
          label="New Password"
          name="new_password"
          hasFeedback
          rules={[
            { required: true, message: "Please input the new password!" },
            {
              min: 8,
              message: "Password must be at least 8 characters long!",
            },
            {
              pattern:
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).+$/,
              message:
                "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character!",
            },
            {
              max: 100,
              message: "Password cannot exceed 100 characters",
            },
          ]}
        >
          <Input.Password maxLength={50} placeholder="New Password" />
        </Form.Item>

        <Form.Item
          label="Confirm New Password"
          name="confirm_new_password"
          dependencies={["new_password"]}
          hasFeedback
          rules={[
            { required: true, message: "Please input the confirm password!" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("new_password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("Passwords that you entered do not match!")
                );
              },
            }),
          ]}
        >
          <Input.Password maxLength={50} placeholder={"Confirm New Password"} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default Header;
